import React, { useCallback, useContext, useEffect, useState } from "react";

import styled from "styled-components";

import { TableContext } from "@sso/context/tableContext";
import { useAppDispatch } from "@sso/hooks/useRedux";
import { BetweenValueSearchColumn, SearchParameterKeys } from "@sso/types";
import { InputLabel, RawInput } from "@sso/ui/RawInput";

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InputSearch = styled(RawInput)`
  width: 100%;
  padding: 0.4rem;
  transition: width 0.5s ease;
  ::placeholder {
    font-size: 1rem;
    font-family: "Roboto";
  }
`;
const TableSearchContainer = styled.form`
  width: 60%;
  max-width: 450px;
  padding: 0 0.2rem;
  display: flex;
  position: relative;
  @media (max-width: ${p => p.theme.breakpoints.sm}) {
    width: 100%;
    max-width: 100%;
  }
`;

interface TableSearchSelectProps {
  column: string;
  name: string;
  startDate?: Date;
  endDate?: Date;
}

export const TableSearchDateRange = ({ column, name, startDate, endDate }: TableSearchSelectProps) => {
  const minimumDate = "2000-01-01";
  const dispatch = useAppDispatch();
  const { search } = useContext(TableContext);

  const [filterStartDate, setFilterStartDate] = useState(startDate);
  const [filterEndDate, setFilterEndDate] = useState(endDate);

  const searchField = search && (search.object[column] as BetweenValueSearchColumn);

  useEffect(() => {
    if (!searchField) {
      setFilterStartDate(undefined);
      setFilterEndDate(undefined);
      return;
    }

    setFilterStartDate(prev => (searchField.startValue && !prev ? new Date(searchField.startValue) : prev));
    setFilterEndDate(prev => (searchField.endValue && !prev ? new Date(searchField.endValue) : prev));
  }, [searchField]);

  const handleSetSearch = useCallback(
    (dateStart: Date | undefined, dateEnd: Date | undefined) => {
      if (!search || (!dateStart && !dateEnd)) {
        return;
      }

      if (dateStart && (dateStart.getFullYear() < 2000 || dateStart.getFullYear() > 9000)) {
        return;
      }

      if (dateEnd && (dateEnd.getFullYear() < 2000 || dateEnd.getFullYear() > 9000)) {
        return;
      }

      const title = `${name}: ${toLocaleDateString(dateStart)} - ${toLocaleDateString(dateEnd)}`;
      dispatch(
        search.onChange({
          column,
          startValue: dateStart?.toISOString(),
          endValue: dateEnd?.toISOString(),
          title,
          parameter: SearchParameterKeys.BETWEEN,
        }),
      );
    },
    [column, dispatch, name, search?.onChange],
  );

  const handleClearSearch = useCallback(() => {
    if (!search) {
      return;
    }
    dispatch(search.onClear(column));
  }, [column, dispatch, search?.onClear]);

  useEffect(() => {
    if (!filterStartDate && !filterEndDate) {
      handleClearSearch();
    } else {
      handleSetSearch(filterStartDate, filterEndDate);
    }
  }, [filterStartDate, filterEndDate, handleClearSearch, handleSetSearch]);

  const tryDateParse = (dateString: string): Date | undefined => {
    try {
      const date = new Date(dateString);
      if (!Number.isNaN(date.getTime())) {
        return date;
      } else {
        return undefined;
      }
    } catch (error) {
      //ignore errors
    }
    return undefined;
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = tryDateParse(e.target.value);
    setFilterStartDate(newStartDate);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = tryDateParse(e.target.value);
    if (newEndDate) {
      newEndDate.setHours(23, 59, 59, 999);
    }
    setFilterEndDate(newEndDate);
  };

  const toLocaleDateString = (date: Date | undefined) => {
    if (!date) {
      return "";
    }
    return date?.toLocaleDateString();
  };

  const toIsoString = (date: Date | undefined) => {
    if (!date) {
      return "";
    }
    return date?.toISOString().split("T")[0];
  };

  return (
    <TableSearchContainer>
      <InputContainer>
        <InputSearch
          type="date"
          name="startDate"
          id="startDate"
          min={minimumDate}
          max={toIsoString(filterEndDate)}
          value={toIsoString(filterStartDate)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleStartDateChange(e)}
        />
        <InputLabel htmlFor="startDate">Start Date</InputLabel>
      </InputContainer>

      <InputContainer style={{ marginLeft: 10 }}>
        <InputSearch
          type="date"
          name="endDate"
          id="endDate"
          min={toIsoString(filterStartDate) || minimumDate}
          value={toIsoString(filterEndDate)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEndDateChange(e)}
        />
        <InputLabel htmlFor="endDate">End Date</InputLabel>
      </InputContainer>
    </TableSearchContainer>
  );
};
